import { useCallback, useRef, useState, useContext } from 'react';

import { DialogError } from '../../../business-error';
import { useDialog } from '../../../dialog/useDialog';
import { ToastNotificationContext } from '../../../toast-notification';
import { handleResponseError } from '../../../../../api/handleResponseError';

import { getDeleteReportBusinessError } from './bussinessErrors';
import { DeletedReport, HandleDeleteReportRequest } from './interfaces';

interface Props {
  handleSuccess: () => void;
  handleDeleteReportRequest: HandleDeleteReportRequest;
}

export const useDeleteReport = ({ handleSuccess, handleDeleteReportRequest }: Props) => {
  const { openToast } = useContext(ToastNotificationContext);
  const [deletingReport, setDeletingReport] = useState<DeletedReport>();
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [isReportDeleting, setLoading] = useState<boolean>(false);
  const [deleteReportError, setError] = useState<DialogError>(null);

  const handleDeleteReport = useCallback(async () => {
    try {
      if (deletingReport) {
        setLoading(true);
        setError(null);

        const response = await handleDeleteReportRequest(deletingReport.id);

        if (response.status !== 204) {
          await handleResponseError(response);
        }

        openToast({
          message: 'Report successfully deleted',
          severity: 'success'
        });

        setLoading(false);
        handleCloseDialog();
        handleSuccess();
      }
    } catch (e) {
      const { message, severity } = getDeleteReportBusinessError(e);

      setError({ message, severity });
      setLoading(false);
    }
  }, [deletingReport, handleCloseDialog, handleDeleteReportRequest, handleSuccess, openToast]);

  const handleOpenDeleteDialog = useRef((report: DeletedReport) => {
    handleOpenDialog();
    setDeletingReport(report);
  });

  const handleCloseDeleteDialog = () => {
    setError(null);
    handleCloseDialog();
  };

  return {
    handleOpenDeleteDialog: handleOpenDeleteDialog.current,
    openDeleteDialog: isDialogOpen,
    handleCloseDeleteDialog,
    handleDeleteReport,
    isReportDeleting,
    deletingReport,
    deleteReportError
  };
};
